import { Controller } from "@hotwired/stimulus";

/**
 * Restaurant Item Override Price Controller
 *
 * This controller manages the visibility of price inputs when a modifier group
 * that REPLACES the item's base price is selected. It toggles between:
 * 1. Base price input (when no base price replacing group or "None" is
 *    selected)
 * 2. Appropriate modifier dropdown (when a specific override group is
 *    selected)
 * 3. The derived, read-only price (when a specific highest group is selected)
 *
 * The two kinds are NOT interchangeable. For an override group the merchant
 * picks which of the group's prices is the base, so the panel is a dropdown.
 * For a highest group the base price is derived by the model (the group's
 * cheapest option, re-synced on every modifier mutation), so the panel is
 * text and there is nothing to submit: whatever a field here offered, the
 * model would overwrite on the way to the database.
 */
export default class extends Controller {
  static targets = [
    "basePriceInput", // The container for the manual base price input
    "modifierGroup", // Containers for each override group's select dropdown
    "highestGroup", // Containers for each highest group's derived price
    "overrideRadio", // Radio buttons for override modifier groups
    "highestRadio", // Radio buttons for highest modifier groups
    "noneRadio", // The "None" radio button option
    "showOverridePrices", // The "show override prices on the card" setting
  ];

  connect() {
    // When the controller connects, set the initial state
    this.togglePriceInput();
  }

  /**
   * Toggle which price input is shown based on radio selection
   */
  togglePriceInput() {
    // Get the selected group ID of each kind, or null
    const selectedOverrideId = this.selectedValue(this.overrideRadioTargets);
    const selectedHighestId = this.selectedValue(this.highestRadioTargets);

    // The merchant types a base price only when nothing else names one: an
    // override group picks it from the group's prices, a highest group
    // derives it from the group's cheapest option.
    this.toggleBasePriceInput(!selectedOverrideId && !selectedHighestId);

    // Handle all modifier group dropdowns
    this.modifierGroupTargets.forEach((group) => {
      const groupId = group.dataset.groupId;
      const select = group.querySelector("select");
      const selected = groupId === selectedOverrideId;

      // Show and enable the selected group's dropdown, hide and disable the
      // rest. Required only while visible.
      group.classList.toggle("hidden", !selected);
      select.disabled = !selected;
      select.required = selected;
    });

    // Handle all derived price panels. They carry no field, so there is
    // nothing to enable or require here.
    this.highestGroupTargets.forEach((group) => {
      const selected = group.dataset.groupId === selectedHighestId;

      group.classList.toggle("hidden", !selected);
    });

    // Printing every option's price on the menu card is an override group's
    // setting only: a highest group holds a dozen varieties.
    this.showOverridePricesTargets.forEach((setting) => {
      setting.classList.toggle("hidden", Boolean(selectedHighestId));
    });
  }

  selectedValue(radios) {
    return radios.find((radio) => radio.checked)?.value || null;
  }

  toggleBasePriceInput(show) {
    this.basePriceInputTarget.classList.toggle("hidden", !show);
    this.basePriceInputTarget.querySelector("input").disabled = !show;
  }
}
